import { Button } from "@eworkit/iris";
import React, { FC, memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import { DialogDisclosure } from "reakit/Dialog";

import { Header } from "@/components/Header";
import { Icon } from "@/components/Icon";
import { Section } from "@/components/Section";
import { Text } from "@/components/Text";
import { TextListWithDot } from "@/components/TextListWithDot";
import { MEDIA_SIZE_SM } from "@/core/media";
import { DCCreateTaskActionType, IDCCreateTask } from "@/data/tasks";
import { Flex, Tooltip } from "@/eworkitComponents/components";
import { useFormats } from "@/hooks/Formats";
import { ApplyModal } from "@/views/job-applications/apply/ApplyModal";
import { useApplyModalState } from "@/views/job-applications/apply/useApplyModalState";
import { RejectTaskModalButton } from "@/views/Tasks/List/components/ConfirmationModals";
import { TaskPriorityLabel } from "@/views/Tasks/List/components/TaskPriorityLabel";

export interface IDCCHeaderProps {
  task: IDCCreateTask;
  refresh?: () => void;
  onAction?: (actionType: DCCreateTaskActionType, comment: string, task: IDCCreateTask) => void;
}

export const DCCHeader: FC<IDCCHeaderProps> = memo(({ task, refresh, onAction, ...props }) => {
  const { t } = useTranslation("session");
  const { formatDate } = useFormats();
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);
  const draftApplication = task.entity.applications.find(application => application.status === "DRAFT");
  const hasDraftApplications = !!draftApplication;
  const startDate = formatDate(task.entity.startDate);
  const endDate = formatDate(task.entity.endDate);

  const applyModalState = useApplyModalState(task.entity);

  const handleApplyModalClosed = useCallback(() => {
    refresh?.();
  }, [refresh]);

  return (
    <Section
      width={12}
      direction="column"
      background="background-card"
      shadow={isSmallScreen ? "none" : "default"}
      borderRadius={!isSmallScreen}
      padding={["md", "lg"]}
      innerBorder
      {...props}>
      <Flex padding={[false, false, "sm", false]} justify="space-between" innerMargin="sm">
        <TaskPriorityLabel priority={task.priority} />

        {hasDraftApplications && (
          <Flex align="center" innerMargin="xs">
            <Icon icon="circle" size="xxs" color="warning" />

            <Text color="grey2">{t("TASKS.DESIGNATED_CANDIDATE_CREATE.DRAFT_HINT")}</Text>
          </Flex>
        )}
      </Flex>

      <Flex
        direction={isSmallScreen ? "column" : "row"}
        padding={["md", false, false, false]}
        innerMargin="md"
        justify="space-between">
        <Flex innerMargin="md">
          <Icon size="xxl" icon="type_job_request" />

          <Flex direction="column" innerMargin="sm">
            <Header weight="bold" tagType="h1">
              {task.entity.title} / {task.entity.designatedCandidate?.workerName}
            </Header>

            <TextListWithDot>
              {!!task.entity.systemId && <div>{task.entity.systemId}</div>}
              <Tooltip
                placement="bottom"
                content={t("TASKS.DESIGNATED_CANDIDATE_CREATE.PERIOD_TOOLTIP", {
                  startDate,
                  endDate,
                })}>
                <Flex key="dates" innerMargin="xs">
                  <Text color="black" text={startDate} />
                  <Icon icon="ic_arrow_forward" color="grey1" />
                  <Text color="black" text={endDate} />
                </Flex>
              </Tooltip>
            </TextListWithDot>
          </Flex>
        </Flex>

        <Flex innerMargin="sm">
          <RejectTaskModalButton
            display={isSmallScreen ? "block" : "inline-block"}
            variant="secondary"
            size="lg"
            onReject={(comment: string) => onAction?.("cancel", comment, task)}>
            {t("TASKS.DESIGNATED_CANDIDATE_CREATE.BUTTON_REJECT")}
          </RejectTaskModalButton>

          <Button
            as={DialogDisclosure}
            display={isSmallScreen ? "block" : "inline-block"}
            {...applyModalState}
            size="lg"
            variant="primary">
            {hasDraftApplications
              ? t("TASKS.DESIGNATED_CANDIDATE_CREATE.BUTTON_FINISH_APPLICATION")
              : t("TASKS.DESIGNATED_CANDIDATE_CREATE.BUTTON_APPLY")}
          </Button>

          {applyModalState.visible && (
            <ApplyModal
              dialogState={applyModalState}
              jobRequest={task.entity}
              designatedCandidate={task.entity.designatedCandidate!.workerName}
              onClosed={handleApplyModalClosed}
            />
          )}
        </Flex>
      </Flex>
    </Section>
  );
});
